import type { ReactNode } from 'react';

import styles from './ContentLoader.module.css';

import type { ActivityIndicatorProps } from '../ActivityIndicator';
import { ActivityIndicator } from '../ActivityIndicator';

export interface ContentLoaderProps extends Omit<
  ActivityIndicatorProps,
  'role'
> {
  /** Is the content loading */
  loading: boolean;
  /**
   * Height of the content container while loading. While loading it's a good
   * idea to set this to about the expected height of the content to avoid
   * shifting elements after loading completes.
   */
  height: number | string;
  /** Class applied to the top level container */
  className?: string;
  children: ReactNode;
}

/**
 * Handle the loading state of your content. While loading, an
 * {@link ActivityIndicator} is rendered inside a fixed height `div`, that
 * follows accessibility ARIA rules.
 *
 * @see https://corgi-x.tfd.engineering/components/contentloader
 */
export function ContentLoader({
  loading,
  height,
  children,
  className,
  ...activityIndicatorProps
}: ContentLoaderProps) {
  return (
    <div
      aria-live="polite"
      aria-busy={loading}
      className={className}
      role="status"
    >
      {loading ? (
        <div className={styles.container} style={{ height }}>
          <ActivityIndicator {...activityIndicatorProps} role="presentation" />
        </div>
      ) : (
        children
      )}
    </div>
  );
}
